import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { SkipBack, Play, Pause, SkipForward } from 'lucide-react';
import { useTheme } from '@app/theme/ThemeContext';
import { useMapState } from './MapStateProvider';
import { Frame, readyForPlayback } from './caching';
import TimelineDaySegment from './TimelineDaySegment';

interface DaySegment {
  readonly date: Date; // Local midnight for this day
  readonly dayOfMonth: number;
  readonly monthName: string;
  readonly startIndex: number;
  readonly endIndex: number;
  readonly leadTimes: Frame[];
}

export default function TimerControl() {
  const {
    state,
    play,
    pause,
    seek,
    cache,
    currentFrame,
    waitingForBuffer,
    isPrefetching,
    setDragging,
  } = useMapState();

  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [isDragging, setIsDragging] = useState(false);
  const [dragFrame, setDragFrame] = useState<number | null>(null);
  const [hoverFrame, setHoverFrame] = useState<number | null>(null);
  const [hoverPercent, setHoverPercent] = useState<number | null>(null);
  const pendingDragFrameRef = useRef<number | null>(null);
  const dragSeekTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const DRAG_SEEK_DEBOUNCE_MS = 120;

  // Group lead times by day
  const daySegments = useMemo(() => {
    if (cache == null || cache.frames.length < 1) return [];

    const segments: DaySegment[] = [];

    const getLocalDayKey = (d: Date): string => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    const pushSegment = (dayKey: string, framesForDay: Frame[], startIndexForDay: number) => {
      const [yStr, mStr, dStr] = dayKey.split('-');
      const y = Number(yStr);
      const m = Number(mStr);
      const d = Number(dStr);
      // Create a local midnight date to avoid UTC shifting
      const dateLocalMidnight = new Date(y, m - 1, d);
      const endIndex = startIndexForDay + framesForDay.length - 1;
      segments.push({
        date: dateLocalMidnight,
        dayOfMonth: dateLocalMidnight.getDate(),
        monthName: dateLocalMidnight.toLocaleDateString('en-US', { month: 'short' }),
        startIndex: startIndexForDay,
        endIndex,
        leadTimes: framesForDay,
      });
    };

    // Walk frames in order and group by local day
    let currentDayKey: string | null = null;
    let currentStartIndex = 0;
    let framesForDay: Frame[] = [];
    let globalIndex = 0;

    for (const frame of cache.frames) {
      const dayKey = getLocalDayKey(frame.datetime);
      if (currentDayKey == null) {
        currentDayKey = dayKey;
        currentStartIndex = globalIndex;
        framesForDay = [frame];
      } else if (dayKey === currentDayKey) {
        framesForDay.push(frame);
      } else {
        pushSegment(currentDayKey, framesForDay, currentStartIndex);
        currentDayKey = dayKey;
        currentStartIndex = globalIndex;
        framesForDay = [frame];
      }
      globalIndex += 1;
    }

    if (currentDayKey != null && framesForDay.length > 0) {
      pushSegment(currentDayKey, framesForDay, currentStartIndex);
    }

    return segments;
  }, [cache]);

  // Helper function to calculate frame position as percentage (0-100)
  // Based on: equal-width days + time-of-day positioning within each day
  const getFramePositionPercent = useCallback(
    (frameIndex: number): number => {
      if (
        !cache ||
        daySegments.length === 0 ||
        frameIndex < 0 ||
        frameIndex >= cache.frames.length
      ) {
        return 0;
      }

      const frame = cache.frames[frameIndex];

      // Find which day segment this frame belongs to
      const segmentIndex = daySegments.findIndex(
        (seg) => frameIndex >= seg.startIndex && frameIndex <= seg.endIndex,
      );

      if (segmentIndex === -1) return 0;

      const segment = daySegments[segmentIndex];
      const dayWidthPercent = 100 / daySegments.length; // Equal width per day
      const dayStartPercent = segmentIndex * dayWidthPercent;

      // Calculate time-of-day percentage (0.0 to 1.0)
      const midnight = new Date(segment.date);
      const nextMidnight = new Date(segment.date);
      nextMidnight.setDate(nextMidnight.getDate() + 1);

      const dayDurationMs = nextMidnight.getTime() - midnight.getTime();
      const frameTimeMs = frame.datetime.getTime() - midnight.getTime();
      const timeOfDayPercent = frameTimeMs / dayDurationMs;

      // Final position: day start + (time-of-day * day width)
      return dayStartPercent + timeOfDayPercent * dayWidthPercent;
    },
    [cache, daySegments],
  );

  // Calculate total timeline length
  const totalFrames = cache == null ? 0 : cache.frames.length;

  // Play/pause functionality
  const togglePlayPause = useCallback(() => {
    if (state === 'PAUSED') {
      play();
    } else {
      pause();
    }
  }, [state, play, pause]);

  const skipBackward = useCallback(() => {
    if (currentFrame == null || cache == null) {
      return;
    }
    seek(Math.max(0, currentFrame - 1));
  }, [currentFrame, cache, seek]);

  const skipForward = useCallback(() => {
    if (currentFrame == null || cache == null) {
      return;
    }
    seek(Math.min(cache.frames.length - 1, currentFrame + 1));
  }, [currentFrame, cache, seek]);

  // Calculate frame from mouse position based on equal-width days
  const calculateFrameFromPosition = useCallback(
    (clientX: number) => {
      if (!timelineRef.current || !cache || cache.frames.length === 0 || daySegments.length === 0) {
        return null;
      }

      const rect = timelineRef.current.getBoundingClientRect();
      const clickX = clientX - rect.left;
      const clickPercent = Math.max(0, Math.min(100, (clickX / rect.width) * 100));

      // Find the closest frame by comparing click position to actual frame positions
      let closestFrameIndex = 0;
      let closestDistance = Math.abs(getFramePositionPercent(0) - clickPercent);

      for (let i = 1; i < cache.frames.length; i++) {
        const framePercent = getFramePositionPercent(i);
        const distance = Math.abs(framePercent - clickPercent);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestFrameIndex = i;
        }
      }

      return closestFrameIndex;
    },
    [cache, daySegments, getFramePositionPercent],
  );

  // Handle timeline click
  const handleTimelineClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (isDragging) return; // Don't handle clicks while dragging

      const newFrame = calculateFrameFromPosition(event.clientX);
      if (newFrame !== null) {
        seek(newFrame);
      }
    },
    [isDragging, calculateFrameFromPosition, seek],
  );

  const handleTimelineMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (isDragging) return;
      if (!timelineRef.current || !cache || cache.frames.length === 0) {
        setHoverFrame(null);
        setHoverPercent(null);
        return;
      }
      const rect = timelineRef.current.getBoundingClientRect();
      const x = Math.max(rect.left, Math.min(event.clientX, rect.right));
      const percent = ((x - rect.left) / rect.width) * 100;
      const newFrame = calculateFrameFromPosition(event.clientX);
      setHoverPercent(Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : null);
      setHoverFrame(newFrame);
    },
    [isDragging, cache, calculateFrameFromPosition],
  );

  const handleTimelineMouseLeave = useCallback(() => {
    setHoverFrame(null);
    setHoverPercent(null);
  }, []);

  // Drag handlers
  const handleIndicatorMouseDown = useCallback(
    (event: React.MouseEvent) => {
      // Don't start drag on right-click
      if (event.button === 2) return;

      event.preventDefault();
      event.stopPropagation();
      setIsDragging(true);
      setDragging(true);

      // Pause playing while dragging
      if (state === 'PLAYING') {
        pause();
      }
    },
    [state, pause, setDragging],
  );

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isDragging) return;

      const newFrame = calculateFrameFromPosition(event.clientX);
      if (newFrame !== null) {
        // Move indicator immediately while dragging
        setDragFrame(newFrame);
        pendingDragFrameRef.current = newFrame;

        // If the frame is already ready, seek immediately without debounce
        if (cache && cache.frames[newFrame] && cache.frames[newFrame].variableData != null) {
          if (dragSeekTimeoutRef.current) {
            clearTimeout(dragSeekTimeoutRef.current);
            dragSeekTimeoutRef.current = null;
          }
          seek(newFrame);
          return;
        }

        // Otherwise, debounce seeking until user pauses dragging
        if (dragSeekTimeoutRef.current) {
          clearTimeout(dragSeekTimeoutRef.current);
        }
        dragSeekTimeoutRef.current = setTimeout(() => {
          const frameToSeek = pendingDragFrameRef.current;
          if (frameToSeek != null) {
            seek(frameToSeek);
          }
        }, DRAG_SEEK_DEBOUNCE_MS);
      }
    },
    [isDragging, calculateFrameFromPosition, seek, cache],
  );

  const handleMouseUp = useCallback(() => {
    // End drag immediately
    setIsDragging(false);
    setDragging(false);

    // Clear any pending debounced seek and commit the final frame
    if (dragSeekTimeoutRef.current) {
      clearTimeout(dragSeekTimeoutRef.current);
      dragSeekTimeoutRef.current = null;
    }
    const finalFrame = pendingDragFrameRef.current;
    if (finalFrame != null) {
      seek(finalFrame);
    }
    pendingDragFrameRef.current = null;
    setDragFrame(null);
  }, [seek, setDragging]);

  // Handle document-level mouse events for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Calculate indicator position based on actual timestamps
  const indicatorPosition = useMemo(() => {
    if (daySegments.length === 0 || totalFrames <= 1 || !cache) return 0;
    const effectiveFrame = isDragging && dragFrame != null ? dragFrame : currentFrame;
    if (effectiveFrame == null) return 0;

    // Use the day-based positioning
    return getFramePositionPercent(effectiveFrame);
  }, [
    daySegments,
    currentFrame,
    totalFrames,
    isDragging,
    dragFrame,
    cache,
    getFramePositionPercent,
  ]);

  // Calculate all cached ranges for visual indicator
  const cachedRanges = useMemo(() => {
    if (daySegments.length === 0 || totalFrames <= 1 || !cache || cache.frames.length === 0) {
      return [];
    }

    // Find all cached frames and group into continuous ranges
    const cachedFrames = cache.frames
      .map((f, i) => (readyForPlayback(f) ? i : null))
      .filter((f) => f != null);

    if (cachedFrames.length === 0) return [];

    const ranges = [];
    let currentRangeStart = cachedFrames[0];
    let currentRangeEnd = cachedFrames[0];

    for (let i = 1; i < cachedFrames.length; i++) {
      const frame = cachedFrames[i];

      // If this frame is consecutive to the current range, extend it
      if (frame === currentRangeEnd + 1) {
        currentRangeEnd = frame;
      } else {
        // Gap found, save current range and start new one
        const startPos = getFramePositionPercent(currentRangeStart);
        const endPos = getFramePositionPercent(currentRangeEnd);
        ranges.push({
          start: startPos,
          end: endPos,
          frameStart: currentRangeStart,
          frameEnd: currentRangeEnd,
        });

        currentRangeStart = frame;
        currentRangeEnd = frame;
      }
    }

    // Add the last range
    const startPos = getFramePositionPercent(currentRangeStart);
    const endPos = getFramePositionPercent(currentRangeEnd);
    ranges.push({
      start: startPos,
      end: endPos,
      frameStart: currentRangeStart,
      frameEnd: currentRangeEnd,
    });

    return ranges;
  }, [daySegments, totalFrames, cache, getFramePositionPercent]);

  if (cache == null || cache.frames.length < 1) {
    return (
      <div className="flex items-center justify-center h-16 text-gray-500">
        No timeline data available
      </div>
    );
  }

  const activeFrame =
    isDragging && dragFrame != null ? dragFrame : currentFrame == null ? 0 : currentFrame;
  const isPlaying = state === 'PLAYING';
  const isLoading = isPrefetching || waitingForBuffer;
  const prefetchProgress = 0;

  return (
    <div className="p-4">
      {/* Current time label above controls */}
      <div className="px-4 mb-2 flex justify-center">
        <div
          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border shadow-sm text-xs font-mono ${
            isDark
              ? 'bg-gray-800 text-gray-100 border-gray-700'
              : 'bg-white text-gray-900 border-gray-200'
          }`}
        >
          <span>
            {cache.frames[activeFrame]
              ? new Date(cache.frames[activeFrame].datetime).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : ''}
          </span>
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-pink-500" />
        </div>
      </div>
      <div
        className={`flex items-center pr-3 w-full h-12 ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        } shadow-sm border rounded-xl`}
      >
        {/* Play Controls */}
        <div className="flex items-center gap-1 px-4">
          <button
            onClick={skipBackward}
            disabled={activeFrame <= 0}
            className={`p-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isDark ? 'hover:bg-gray-700 text-gray-100' : 'hover:bg-gray-100 text-gray-900'
            }`}
            title="Previous frame"
          >
            <SkipBack size={18} />
          </button>

          <div className="relative">
            <button
              onClick={togglePlayPause}
              className={`p-2 rounded-md transition-colors ${
                isDark ? 'hover:bg-gray-700 text-gray-100' : 'hover:bg-gray-100 text-gray-900'
              }`}
              title={
                isPrefetching
                  ? 'Loading tiles...'
                  : waitingForBuffer
                    ? 'Loading frames for smooth playback...'
                    : `${isPlaying ? 'Pause' : 'Play'} (${prefetchProgress}% buffered)`
              }
            >
              {isLoading ? (
                <div
                  className={`w-[18px] h-[18px] rounded-full border-2 border-t-transparent animate-spin ${
                    isDark ? 'border-gray-300' : 'border-gray-600'
                  }`}
                />
              ) : isPlaying ? (
                <Pause size={18} />
              ) : (
                <Play size={18} />
              )}
            </button>
            {/* Prefetch progress indicator */}
            {prefetchProgress < 100 && prefetchProgress > 0 && (
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gray-300 rounded-full overflow-hidden">
                <div
                  className="h-full bg-pink-500 transition-all duration-300"
                  style={{ width: `${prefetchProgress}%` }}
                />
              </div>
            )}
          </div>

          <button
            onClick={skipForward}
            disabled={activeFrame >= totalFrames - 1}
            className={`p-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isDark ? 'hover:bg-gray-700 text-gray-100' : 'hover:bg-gray-100 text-gray-900'
            }`}
            title="Next frame"
          >
            <SkipForward size={18} />
          </button>
        </div>

        {/* Timeline */}
        <div className="flex-1 relative h-full">
          {/* Timeline background */}
          <div
            ref={timelineRef}
            className={`absolute inset-0 ${isDragging ? 'cursor-grabbing' : 'cursor-pointer'}`}
            onClick={handleTimelineClick}
            onMouseMove={handleTimelineMouseMove}
            onMouseLeave={handleTimelineMouseLeave}
          >
            {/* Daily segments - equal width per day */}
            <div className="flex h-full">
              {daySegments.map((segment, index) => (
                <TimelineDaySegment
                  key={segment.date.toISOString()}
                  date={segment.date}
                  dayOfMonth={segment.dayOfMonth}
                  monthName={segment.monthName}
                  widthPercent={100 / daySegments.length}
                  isLast={index === daySegments.length - 1}
                  isDark={isDark}
                />
              ))}
            </div>

            {/* All cached ranges indicator */}
            {cachedRanges.map((range) => {
              const includesCurrentFrame =
                activeFrame >= range.frameStart && activeFrame <= range.frameEnd;
              return (
                <div
                  key={`cached-range-${range.frameStart}-${range.frameEnd}`}
                  className={`absolute top-0 bottom-0 z-5 pointer-events-none rounded ${
                    includesCurrentFrame
                      ? isDark
                        ? 'bg-pink-500/25'
                        : 'bg-pink-500/20'
                      : isDark
                        ? 'bg-pink-500/15'
                        : 'bg-pink-500/10'
                  }`}
                  style={{
                    left: `${range.start}%`,
                    width: `${range.end - range.start}%`,
                  }}
                  title={`Cached frames ${range.frameStart} - ${range.frameEnd}`}
                >
                  {/* Subtle gradient overlay, stronger for current range */}
                  <div
                    className={`absolute inset-0 rounded ${
                      includesCurrentFrame
                        ? isDark
                          ? 'bg-gradient-to-r from-pink-500/15 to-pink-500/35'
                          : 'bg-gradient-to-r from-pink-500/10 to-pink-500/25'
                        : isDark
                          ? 'bg-gradient-to-r from-pink-500/5 to-pink-500/20'
                          : 'bg-gradient-to-r from-pink-500/3 to-pink-500/15'
                    }`}
                  />
                  {/* Subtle top border, stronger for current range */}
                  <div
                    className={`absolute top-0 left-0 right-0 h-0.5 rounded-t ${
                      includesCurrentFrame
                        ? isDark
                          ? 'bg-pink-400/80'
                          : 'bg-pink-500/60'
                        : isDark
                          ? 'bg-pink-400/40'
                          : 'bg-pink-500/30'
                    }`}
                  />
                </div>
              );
            })}

            {/* Current position indicator */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-pink-500 z-10 pointer-events-none"
              style={{ left: `${indicatorPosition}%` }}
            >
              {/* Draggable small round handle */}
              <div
                className={`absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 ${
                  isDragging ? 'cursor-grabbing' : 'cursor-grab'
                } z-20`}
                style={{ pointerEvents: 'auto' }}
                onMouseDown={handleIndicatorMouseDown}
              >
                <div className="w-3 h-3 rounded-full bg-pink-500 border-2 border-white shadow-md" />
              </div>
            </div>

            {/* Hover indicator and tooltip */}
            {hoverPercent != null && hoverFrame != null && cache?.frames[hoverFrame] && (
              <div
                className="absolute top-0 bottom-0 z-20 pointer-events-none"
                style={{ left: `${hoverPercent}%` }}
              >
                <div className="absolute top-0 bottom-0 w-0.5 bg-pink-400/70" />
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full">
                  <div
                    className={`relative whitespace-nowrap rounded-md px-2 py-0.5 text-[11px] font-mono shadow-md border pointer-events-none ${
                      isDark
                        ? 'bg-gray-800 text-gray-100 border-gray-700'
                        : 'bg-white text-gray-900 border-gray-200'
                    }`}
                  >
                    {new Date(cache.frames[hoverFrame].datetime).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    <div className="pointer-events-none absolute left-1/2 top-full">
                      <div className="-ml-[6px] w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-pink-600" />
                      <div className="-ml-[5px] -mt-[7px] w-0 h-0 border-l-[5px] border-r-[5px] border-t-[7px] border-l-transparent border-r-transparent border-t-pink-500" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
